import React from 'react';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { setFlowStatus, submitData } from '../../actions/userData';
import { scrollTop } from '../../animation/scrollTo';
import { getCurrentAgeYears, getMaritalStatus } from '../../calculations/userDataTransformer';
import { PORTFOLIO_LINK, SOCIAL_BENEFITS_LINK, SOCIAL_CHOICE_LINK } from '../flows/linkConstants';
import { ConnectedFlow } from './ConnectedFlow';
import SocialSecurityChoiceFlow from './SocialSecurityChoiceFlow';

function makeSocialSecurityQuestions(prefix, userName) {
    return [
        {
            questions: [
            ],
            shouldShow: (answers) => true,
            text: 'Enter details about ' + userName + '\'s estimated social security benefit:',
            descriptor: 'Social Security Benefits'
        },
        {
            questions: [
                {
                    id: prefix + 'Health', description: 'Health Condition', type: 'select', text: `How is ${userName}'s health?`, options: [
                        { id: 'poor', label: 'Poor' },
                        { id: 'average', label: 'Average' },
                        { id: 'excellent', label: 'Excellent' },
                    ],
                    help: "Your long term health is a major factor when considering when to begin receiving Social Security benefits",
                }
            ],
            shouldShow: (answers) => true,
            descriptor: 'Health',
        },
        {
            questions: [
                { id: prefix + 'Taking', description: userName + '\'s taking social security', type: 'boolean', text: `Is ${userName} presently taking social security?` },
            ],
            shouldShow: (answers) => getCurrentAgeYears(answers[prefix + 'Dob']) >= 62,
        },
        {
            questions: [
                { id: prefix + 'WorkedForNonCovered', description: 'Non-Covered Employment', type: 'boolean', text: `Does ${userName} fall under any of these rules?`, explaination: <img style={{ width: "100%", objectFit: "cover" }} src={"/wephelp.png"} alt="Windfall Elemination Provision reference chart" />, },
            ],
            shouldShow: (answers) => answers[prefix + 'Taking'] === false && answers[prefix + 'WorkedForGovernment'] === true,
        },
        {
            questions: [
                { id: prefix + 'NumYearsSubstantialEarnings', description: 'Years of Substantial Earnings', type: 'number', text: `How many years of substantial earnings did ${userName} did pay into social security?` },
            ],
            shouldShow: (answers) => !answers[prefix + 'Taking'] && answers[prefix + 'WorkedForNonCovered'] === true,
        },
        {
            questions: [
                { id: prefix + 'FRABenefit', description: userName + '\'s monthly benefit', type: 'dollar', text: `What is ${userName}'s monthly benefit at Full Retirement Age?`, explaination: <img style={{ width: "100%", objectFit: "cover" }} src={"/frahelp.png"} alt="FRA payment estimate" /> },
            ],
            shouldShow: (answers) => true,
        },
    ]
}

function previouslyMarriedQuestions(maritalStatus) {
    return [
        {
            questions: [
                { id: 'prevMarriedLongerThanTenYears', description: 'Were you married to any previous spouse for more than 10 years?', type: 'boolean', text: `Were you married to any previous spouse for more than 10 years?` },
            ],
            shouldShow: (answers) => maritalStatus === 'divorced',
            text: 'Enter details regarding your ex-spouse(s):',
            descriptor: 'Divorce Details'
        },
        {
            questions: [
                { id: 'divorcedMarriageHighestBenefit', description: 'Highest Benefit', type: 'dollar', text: 'What is the highest Social Security benefit any of your ex-spouses (that you were married to for more than 10 years) would receive at their FRA (Full Retirement Age).', help: 'If you were married to any one person for more than 10 years, you are entitled to receive benefits from their social security when you reach eligible age. If this applies to multiple marriages, enter the benefit for the partner with the highest social security benefit.' },
            ],
            shouldShow: (answers) => getMaritalStatus(answers) === 'divorced' && answers.prevMarriedLongerThanTenYears === true,
        },
        {
            questions: [
                { id: 'divorcedMarriageName', description: 'Reference Name', type: 'string', text: 'What is the name of this partner?' },
            ],
            shouldShow: (answers) => getMaritalStatus(answers) === 'divorced' && answers.prevMarriedLongerThanTenYears === true,
        },
        {
            questions: [
                { id: 'divorcedMarriageYear', description: 'Divorce Date', type: 'dob', text: 'When were you divorced from this partner?' },
            ],
            shouldShow: (answers) => getMaritalStatus(answers) === 'divorced' && answers.prevMarriedLongerThanTenYears === true,
        },
    ]
}

function widowedQuestions(maritalStatus) {
    return [
        {
            questions: [
                { id: 'passedAwaySpouseBenefit', description: 'Late Spouse\'s Benefit', type: 'dollar', text: `What would your deceased spouse's FRA benefit have been?` },
            ],
            shouldShow: () => true,
            text: 'Enter details on your late Spouse:',
            descriptor: 'Late Spouse Details'
        },
    ]
}

const SocialSecurityFlow = ({ userName, spouseName, maritalStatus, subTab, setFlowStatus }) => {
    const history = useHistory();

    if (subTab === 'choice') return <SocialSecurityChoiceFlow />
    if (subTab === 'expected-benefits') {
        let sections = makeSocialSecurityQuestions('main', userName);
        if (maritalStatus === 'married') {
            sections = sections.concat(makeSocialSecurityQuestions('spouse', spouseName));
        } else if (maritalStatus === 'divorced') {
            sections = sections.concat(previouslyMarriedQuestions(maritalStatus));
        } else if (maritalStatus === 'widowed') {
            sections = sections.concat(widowedQuestions());
        }

        return <div style={{ width: '100%' }}>
            <ConnectedFlow questionSections={sections} flowMode={true} finishFlow={() => {
                scrollTop();
                submitData()
                setFlowStatus({ name: subTab, status: 'finished' });
                setFlowStatus({ name: 'choice', status: 'pending' });
                history.push(SOCIAL_CHOICE_LINK);
            }} goBack={() => {
                scrollTop();
                history.push(PORTFOLIO_LINK);
            }} />
        </div>
    }

    return <Redirect to={SOCIAL_BENEFITS_LINK} />

}

export default connect(state => {
    let maritalStatus = state.userData.answers.maritalStatus[0].id;
    return {
        userName: state.userData.answers.mainUserName,
        spouseName: state.userData.answers.spouseUserName,
        maritalStatus,
    }
}, {
    setFlowStatus,
    submitData,
})(SocialSecurityFlow);
